import type { StaticNavigationNavigationItemDto } from '@ori/static-api-client-react';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useIsMobile } from '../../hooks/useIsMobile';
import { usePbsContent } from './pbsContentContext';

export type ActiveLink = StaticNavigationNavigationItemDto | null;

export interface ActiveLinkContext {
  activeLink: ActiveLink;
  toggleActiveLink: (link: ActiveLink) => void;
}

export const activeLinkContext = createContext<ActiveLinkContext | undefined>(undefined);

const { Provider } = activeLinkContext;

export const ActiveLinkProvider: FC<PropsWithChildren> = ({ children }) => {
  const [activeLink, setActiveLink] = useState<ActiveLink>(null);
  const { setIsShown } = usePbsContent();
  const isMobile = useIsMobile();
  const toggleActiveLink = useCallback(
    (link: ActiveLink) => {
      setIsShown(false);
      if (isMobile && activeLink === link) {
        setActiveLink(null);
      } else {
        setActiveLink(link);
      }
    },
    [activeLink, isMobile, setIsShown],
  );
  const value = useMemo(() => ({ activeLink, toggleActiveLink }), [activeLink, toggleActiveLink]);

  return <Provider value={value}>{children}</Provider>;
};

export const useActiveLink = () => {
  const context = useContext(activeLinkContext);
  if (context) {
    return context;
  }
  throw new Error('useActiveLinkContext must be used within a ActiveLinkProvider.');
};
